/**
 * SentinelOverlay — Ghost Navigator
 *
 * A non-intrusive "Workstream Overlay" that surfaces proactive navigation hints
 * from the Sentinel background agent. Instead of a chat bubble, it renders a
 * subtle floating Action Card near the bottom-right of the screen.
 *
 * The overlay:
 *  - Opens a WebSocket to /ws/sentinel/{user_id} on mount
 *  - Periodically POSTs the current page/activity to /api/sentinel/state
 *  - Animates in a hint card when the server pushes a sentinel_hint event
 *  - On "Yes" navigates programmatically and initialises the next agent with
 *    the current matter context (passed via window.sentinelMatterContext)
 *  - On "Dismiss" calls /api/sentinel/dismiss to suppress further hints
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, Search, Shield, Users, X, Zap } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';

// Map icon names from the server to Lucide components
const ICON_MAP = {
  users: Users,
  shield: Shield,
  eye: Eye,
  zap: Zap,
  'arrow-right': ArrowRight,
  search: Search,
};

// Map app routes to a canonical "page" key the server understands
function routeToPage(pathname) {
  if (pathname.startsWith('/war-room')) return 'war-room';
  if (pathname.startsWith('/research')) return 'research';
  if (pathname.startsWith('/jolly')) return 'jolly';
  if (pathname.startsWith('/aina')) return 'aina';
  if (pathname.startsWith('/knowledge-base')) return 'knowledge-base';
  if (pathname.startsWith('/knowledge')) return 'knowledge';
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/brief')) return 'brief';
  if (pathname.startsWith('/saved')) return 'saved';
  return 'other';
}

export default function SentinelOverlay() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [hint, setHint] = useState(null);   // current hint to display
  const [visible, setVisible] = useState(false);
  const wsRef = useRef(null);
  const stateIntervalRef = useRef(null);
  const dismissTimer = useRef(null);

  // ── helpers ──────────────────────────────────────────────────────────────

  const postState = useCallback(async (page, activity = 'viewing') => {
    if (!user) return;
    try {
      await fetch(`${API}/sentinel/state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ page, activity }),
      });
    } catch {
      // Non-critical; swallow silently
    }
  }, [user, getAuthHeaders]);

  const dismissHint = useCallback(async () => {
    setVisible(false);
    clearTimeout(dismissTimer.current);
    setTimeout(() => setHint(null), 400); // wait for fade-out
    try {
      await fetch(`${API}/sentinel/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ suppress_seconds: 300 }),
      });
    } catch {
      // Non-critical
    }
  }, [getAuthHeaders]);

  const handleAccept = useCallback(() => {
    if (!hint) return;
    const { navigate_to, params } = hint.action;
    dismissHint();

    if (navigate_to) {
      // Pass matter context if available (set by pages via window global)
      const matterCtx = window.sentinelMatterContext || {};
      const qs = new URLSearchParams();
      if (matterCtx.matterId) qs.set('matterId', matterCtx.matterId);
      Object.entries(params || {}).forEach(([k, v]) => qs.set(k, v));
      const queryString = qs.toString();
      navigate(navigate_to + (queryString ? `?${queryString}` : ''));
    } else if (params?.tab) {
      // Tab switch within the current page — dispatch a custom event
      window.dispatchEvent(new CustomEvent('sentinel-switch-tab', { detail: params }));
    }
  }, [hint, dismissHint, navigate]);

  // ── WebSocket setup ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.user_id) return;

    const backendUrl = API.replace('/api', '');
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/sentinel/${user.user_id}`;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'sentinel_hint') {
            setHint(data);
            setVisible(true);
            // Auto-dismiss after 20 s if user doesn't interact
            clearTimeout(dismissTimer.current);
            dismissTimer.current = setTimeout(dismissHint, 20000);
          }
        } catch {
          // malformed message — ignore
        }
      };

      ws.onclose = () => {
        // Reconnect after 5 s
        setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      wsRef.current?.close();
      clearTimeout(dismissTimer.current);
    };
  }, [user?.user_id, dismissHint]);

  // ── State reporting on route change ─────────────────────────────────────

  useEffect(() => {
    const page = routeToPage(location.pathname);
    postState(page);

    // Report state every 45 s while on the same page
    clearInterval(stateIntervalRef.current);
    stateIntervalRef.current = setInterval(() => postState(page), 45000);

    return () => clearInterval(stateIntervalRef.current);
  }, [location.pathname, postState]);

  // ── Expose activity reporter for pages ──────────────────────────────────
  // Pages call: window.sentinelReportActivity('sabha_generated')

  useEffect(() => {
    const page = routeToPage(location.pathname);
    window.sentinelReportActivity = (activity) => postState(page, activity);
    return () => { delete window.sentinelReportActivity; };
  }, [location.pathname, postState]);

  // ── Don't render on public/auth routes ──────────────────────────────────

  if (!user) return null;
  if (['/auth', '/', '/onboarding', '/share'].some(p => location.pathname.startsWith(p))) return null;
  if (!hint) return null;

  const IconComponent = ICON_MAP[hint.icon] || Zap;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '28px',
        right: '24px',
        zIndex: 9999,
        maxWidth: '340px',
        width: '100%',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.96)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div
        style={{
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-default)',
          borderRadius: '20px',
          padding: '16px 18px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <div
            style={{
              flexShrink: 0,
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: 'rgba(139,92,246,0.14)',
              border: '1px solid rgba(139,92,246,0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent size={15} style={{ color: '#8b5cf6' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8b5cf6', fontWeight: 700, marginBottom: '3px' }}>
              Sentinel
            </div>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.55 }}>
              {hint.suggestion}
            </p>
          </div>
          <button
            onClick={dismissHint}
            aria-label="Dismiss hint"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px', flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Actions */}
        {hint.action && (hint.action.navigate_to || hint.action.params?.tab) && (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={dismissHint}
              style={{
                padding: '6px 14px',
                borderRadius: '10px',
                background: 'var(--bg-base)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Not now
            </button>
            <button
              onClick={handleAccept}
              style={{
                padding: '6px 14px',
                borderRadius: '10px',
                background: '#8b5cf6',
                border: 'none',
                color: '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              Let&apos;s go <ArrowRight size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
